import { useEffect, useMemo, useState, type FormEvent } from "react";
import {
  api,
  type AccountHealth,
  type MailAccountDetail,
  type Preferences,
  type PublicUser,
} from "../../lib/api";
import { AccountWizard } from "../mail/AccountWizard";
import { useToast } from "../shell/Toast";
import {
  playMailClack,
  requestNotifyPermission,
  setNotifyDesktopEnabled,
  setNotifySoundEnabled,
} from "../../lib/notifications";

type TabId =
  | "appearance"
  | "accounts"
  | "compose"
  | "sync"
  | "privacy"
  | "shortcuts"
  | "admin";

const TABS: Array<{ id: TabId; label: string; desc: string; admin?: boolean }> = [
  { id: "appearance", label: "Aspetto", desc: "Tema, densità, layout" },
  { id: "accounts", label: "Account", desc: "IMAP, OAuth, salute" },
  { id: "compose", label: "Composizione", desc: "Firme e mittenti" },
  { id: "sync", label: "Sync & protocolli", desc: "IDLE, JMAP, cache" },
  { id: "privacy", label: "Privacy", desc: "Immagini e dati" },
  { id: "shortcuts", label: "Scorciatoie", desc: "Tastiera e comandi" },
  { id: "admin", label: "Amministrazione", desc: "Utenti e audit", admin: true },
];

const THEMES = [
  {
    id: "dawn",
    label: "Chiaro",
    hint: "Grigi freddi · lettura mail",
    preview: ["#e8ecf1", "#f7f9fb", "#1f6f63"],
  },
  {
    id: "mist",
    label: "Nebbia",
    hint: "Blu-grigio soft",
    preview: ["#e4e9f2", "#f5f7fb", "#2b5ea7"],
  },
  {
    id: "ink",
    label: "Inchiostro",
    hint: "Scuro classico",
    preview: ["#0b0f14", "#111821", "#c4a574"],
  },
  {
    id: "midnight",
    label: "Mezzanotte",
    hint: "Scuro teal",
    preview: ["#06090d", "#0c1218", "#4db6ac"],
  },
] as const;

export function SettingsWorkspace({
  user,
  prefs,
  onPrefs,
}: {
  user: PublicUser;
  prefs: Preferences | null;
  onPrefs: (p: Preferences) => void;
}) {
  const toast = useToast();
  const [tab, setTab] = useState<TabId>("appearance");
  const [health, setHealth] = useState<AccountHealth[]>([]);
  const [accounts, setAccounts] = useState<Awaited<ReturnType<typeof api.accounts>>["accounts"]>([]);
  const [signatures, setSignatures] = useState<
    Array<{ id: string; accountId: string | null; name: string; html: string; isDefault: boolean }>
  >([]);
  const [adminUsers, setAdminUsers] = useState<
    Array<{ id: string; email: string; displayName: string | null; role: string; createdAt?: string }>
  >([]);
  const [audit, setAudit] = useState<unknown[]>([]);
  const [msg, setMsg] = useState("");
  const [blockRemote, setBlockRemote] = useState(() => localStorage.getItem("mailstorm.blockRemote") !== "0");
  const [compactList, setCompactList] = useState(() => localStorage.getItem("mailstorm.compactList") === "1");
  const [notifyDesktop, setNotifyDesktop] = useState(() => localStorage.getItem("mailstorm.notifyDesktop") !== "0");
  const [notifySound, setNotifySound] = useState(() => localStorage.getItem("mailstorm.notifySound") !== "0");
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [edit, setEdit] = useState<MailAccountDetail | null>(null);
  const [editPassword, setEditPassword] = useState("");
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState("");
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [userForm, setUserForm] = useState({
    email: "",
    displayName: "",
    role: "user",
    password: "",
  });
  const [userBusy, setUserBusy] = useState(false);
  const [userError, setUserError] = useState("");

  async function refreshAccounts() {
    const a = await api.accounts();
    setAccounts(a.accounts);
    try {
      const h = await api.health();
      setHealth(h.items);
    } catch {
      /* ignore */
    }
  }

  async function openEdit(id: string) {
    setEditingId(id);
    setEditError("");
    setEditPassword("");
    setEditBusy(true);
    try {
      const { account } = await api.account(id);
      setEdit(account);
    } catch (e) {
      setEditError(e instanceof Error ? e.message : String(e));
      setEdit(null);
    } finally {
      setEditBusy(false);
    }
  }

  async function saveEdit(e: FormEvent) {
    e.preventDefault();
    if (!edit || !editingId) return;
    setEditBusy(true);
    setEditError("");
    try {
      const body: Record<string, unknown> = {
        displayName: edit.displayName || null,
        color: edit.color,
      };
      if (!edit.hasOAuth) {
        body.imap = {
          host: edit.imapHost,
          port: edit.imapPort,
          secure: edit.imapSecure,
        };
        body.smtp = {
          host: edit.smtpHost,
          port: edit.smtpPort,
          secure: edit.smtpSecure,
        };
        if (editPassword.trim()) body.password = editPassword.trim();
      }
      await api.updateAccount(editingId, body);
      toast.push({ message: "Account aggiornato" });
      setEditingId(null);
      setEdit(null);
      setEditPassword("");
      await refreshAccounts();
    } catch (err) {
      setEditError(err instanceof Error ? err.message : String(err));
    } finally {
      setEditBusy(false);
    }
  }

  const visibleTabs = useMemo(
    () => TABS.filter((t) => !t.admin || user.role === "admin"),
    [user.role],
  );

  useEffect(() => {
    (async () => {
      const a = await api.accounts();
      setAccounts(a.accounts);
      try {
        const h = await api.health();
        setHealth(h.items);
      } catch {
        setHealth([]);
      }
      try {
        const s = await api.signatures();
        setSignatures(s.signatures);
      } catch {
        setSignatures([]);
      }
      if (user.role === "admin") {
        const u = await api.adminUsers();
        setAdminUsers(u.users);
        const ev = await api.adminAudit();
        setAudit(ev.events);
      }
      const params = new URLSearchParams(window.location.search);
      if (params.get("oauth") === "ok") {
        setMsg(`OAuth collegato: ${params.get("provider")}`);
        setTab("accounts");
      }
      if (params.get("oauth") === "error") {
        setMsg(params.get("msg") || "OAuth fallito");
        setTab("accounts");
      }
    })();
  }, [user.role]);

  async function savePrefs(patch: Partial<Preferences>) {
    const next = {
      theme: patch.theme ?? prefs?.theme ?? "dawn",
      density: patch.density ?? prefs?.density ?? "comfortable",
      readingPane: patch.readingPane ?? prefs?.readingPane ?? "right",
      defaultAccountId:
        patch.defaultAccountId !== undefined ? patch.defaultAccountId : prefs?.defaultAccountId ?? null,
    };
    await api.savePreferences(next);
    onPrefs(next);
    document.documentElement.setAttribute("data-theme", next.theme || "dawn");
    toast.push({ message: "Preferenze salvate" });
  }

  async function createUser(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    try {
      await api.adminCreateUser({
        email: String(fd.get("email") || ""),
        password: String(fd.get("password") || ""),
        displayName: String(fd.get("displayName") || "") || undefined,
        role: String(fd.get("role") || "user"),
      });
      const u = await api.adminUsers();
      setAdminUsers(u.users);
      e.currentTarget.reset();
      toast.push({ message: "Utente creato" });
    } catch (err) {
      toast.push({ message: err instanceof Error ? err.message : String(err) });
    }
  }

  function openUserEdit(u: { id: string; email: string; displayName: string | null; role: string }) {
    setEditingUserId(u.id);
    setUserError("");
    setUserForm({
      email: u.email,
      displayName: u.displayName || "",
      role: u.role === "admin" ? "admin" : "user",
      password: "",
    });
  }

  async function saveUserEdit(e: FormEvent) {
    e.preventDefault();
    if (!editingUserId) return;
    setUserBusy(true);
    setUserError("");
    try {
      const body: Record<string, unknown> = {
        email: userForm.email.trim(),
        displayName: userForm.displayName.trim() || null,
        role: userForm.role,
      };
      if (userForm.password.trim()) body.password = userForm.password.trim();
      await api.adminUpdateUser(editingUserId, body);
      const u = await api.adminUsers();
      setAdminUsers(u.users);
      toast.push({
        message:
          editingUserId === user.id
            ? "Profilo aggiornato (anche la tua password, se impostata)"
            : "Utente aggiornato",
      });
      setEditingUserId(null);
      setUserForm({ email: "", displayName: "", role: "user", password: "" });
    } catch (err) {
      setUserError(err instanceof Error ? err.message : String(err));
    } finally {
      setUserBusy(false);
    }
  }

  async function deleteUser(id: string, email: string) {
    if (id === user.id) {
      toast.push({ message: "Non puoi eliminare te stesso" });
      return;
    }
    if (!window.confirm(`Eliminare l’utente ${email}?`)) return;
    try {
      await api.adminDeleteUser(id);
      setAdminUsers((prev) => prev.filter((x) => x.id !== id));
      if (editingUserId === id) setEditingUserId(null);
      toast.push({ message: "Utente eliminato" });
    } catch (err) {
      toast.push({ message: err instanceof Error ? err.message : String(err) });
    }
  }

  const active = visibleTabs.find((t) => t.id === tab) || visibleTabs[0]!;

  return (
    <div className="settings-shell">
      <nav className="settings-nav" aria-label="Sezioni impostazioni">
        <div className="settings-nav-title">Impostazioni</div>
        {visibleTabs.map((t) => (
          <button
            key={t.id}
            className={`settings-tab ${tab === t.id ? "active" : ""}`}
            onClick={() => setTab(t.id)}
          >
            <strong>{t.label}</strong>
            <em>{t.desc}</em>
          </button>
        ))}
      </nav>

      <div className="settings-panel">
        <div className="settings-panel-head">
          <h2>{active.label}</h2>
          <p>{active.desc}</p>
        </div>

        {tab === "appearance" && (
          <div className="settings-blocks">
            <section className="settings-block">
              <h3>Tema workspace</h3>
              <p className="help">Tema chiaro di default: contrasto alto per leggere tante mail.</p>
              <div className="theme-swatches">
                {THEMES.map((t) => (
                  <button
                    key={t.id}
                    className={`theme-swatch ${(prefs?.theme || "dawn") === t.id ? "active" : ""}`}
                    onClick={() => void savePrefs({ theme: t.id })}
                  >
                    <div className="theme-preview" aria-hidden>
                      <span style={{ background: t.preview[0] }} />
                      <span style={{ background: t.preview[1] }} />
                      <span style={{ background: t.preview[2] }} />
                    </div>
                    <strong>{t.label}</strong>
                    <span style={{ color: "var(--muted)", fontSize: "0.75rem" }}>{t.hint}</span>
                  </button>
                ))}
              </div>
            </section>

            <section className="settings-block">
              <h3>Layout lettura</h3>
              <div className="pref-row">
                <div>
                  <strong>Pannello lettura</strong>
                  <span>Dove mostrare il corpo del messaggio</span>
                </div>
                <select
                  className="select"
                  style={{ width: 160 }}
                  value={prefs?.readingPane || "right"}
                  onChange={(e) => void savePrefs({ readingPane: e.target.value })}
                >
                  <option value="right">Destra</option>
                  <option value="below">Sotto</option>
                  <option value="off">Solo lista</option>
                </select>
              </div>
              <div className="pref-row">
                <div>
                  <strong>Densità</strong>
                  <span>Spaziatura della lista messaggi</span>
                </div>
                <select
                  className="select"
                  style={{ width: 160 }}
                  value={prefs?.density || "comfortable"}
                  onChange={(e) => void savePrefs({ density: e.target.value })}
                >
                  <option value="comfortable">Comoda</option>
                  <option value="compact">Compatta</option>
                </select>
              </div>
              <div className="pref-row">
                <div>
                  <strong>Lista compatta locale</strong>
                  <span>Anteprime più strette (solo questo browser)</span>
                </div>
                <button
                  className={`btn ${compactList ? "btn-primary" : ""}`}
                  onClick={() => {
                    const next = !compactList;
                    setCompactList(next);
                    localStorage.setItem("mailstorm.compactList", next ? "1" : "0");
                    document.documentElement.setAttribute("data-compact", next ? "1" : "0");
                    toast.push({ message: next ? "Lista compatta on" : "Lista compatta off" });
                  }}
                >
                  {compactList ? "On" : "Off"}
                </button>
              </div>
            </section>
          </div>
        )}

        {tab === "accounts" && (
          <div className="settings-blocks">
            <section className="settings-block">
              <div className="settings-block-head">
                <div>
                  <h3>Account collegati</h3>
                  <p className="help">Clicca un account per modificarlo. Aggiungi IMAP o OAuth da qui.</p>
                </div>
                <button className="btn btn-primary" type="button" onClick={() => setWizardOpen(true)}>
                  Aggiungi account
                </button>
              </div>
              <div style={{ display: "grid", gap: "0.55rem" }}>
                {accounts.map((a) => {
                  const h = health.find((x) => x.accountId === a.id);
                  const isOpen = editingId === a.id;
                  return (
                    <div key={a.id} className={`account-row ${isOpen ? "active" : ""}`}>
                      <button
                        type="button"
                        className="account-row-main"
                        onClick={() => {
                          if (isOpen) {
                            setEditingId(null);
                            setEdit(null);
                          } else {
                            void openEdit(a.id);
                          }
                        }}
                      >
                        <span className="dot" style={{ background: a.color }} />
                        <div>
                          <strong>{a.displayName || a.email}</strong>
                          <div style={{ color: "var(--muted)", fontSize: "0.85rem" }}>
                            {a.email} · {a.provider} · {a.imapHost}
                            {a.hasOAuth ? " · OAuth" : ""}
                            {h && (
                              <span
                                className={
                                  h.status === "ok"
                                    ? "health-ok"
                                    : h.status === "warn"
                                      ? "health-warn"
                                      : "health-bad"
                                }
                              >
                                {" "}
                                · {h.status}: {h.message}
                              </span>
                            )}
                          </div>
                        </div>
                      </button>
                      <div className="actions">
                        <button
                          className="btn"
                          type="button"
                          onClick={() => void openEdit(a.id)}
                        >
                          Modifica
                        </button>
                        <button
                          className="btn"
                          type="button"
                          onClick={() => void savePrefs({ defaultAccountId: a.id })}
                          title="Account predefinito invio"
                        >
                          Default
                        </button>
                        <button
                          className="btn btn-danger"
                          type="button"
                          onClick={async () => {
                            if (!window.confirm(`Rimuovere ${a.email}?`)) return;
                            await api.deleteAccount(a.id);
                            if (editingId === a.id) {
                              setEditingId(null);
                              setEdit(null);
                            }
                            setAccounts((prev) => prev.filter((x) => x.id !== a.id));
                          }}
                        >
                          Rimuovi
                        </button>
                      </div>
                    </div>
                  );
                })}
                {accounts.length === 0 && (
                  <p style={{ color: "var(--muted)" }}>Nessun account — usa «Aggiungi account».</p>
                )}
              </div>

              {editingId && (
                <form className="account-edit form" onSubmit={(e) => void saveEdit(e)}>
                  <h3>Modifica informazioni</h3>
                  {editBusy && !edit && <p className="help">Caricamento…</p>}
                  {editError && <p className="error">{editError}</p>}
                  {edit && (
                    <>
                      <label>
                        Email
                        <input className="input" value={edit.email} disabled />
                      </label>
                      <label>
                        Nome visualizzato
                        <input
                          className="input"
                          value={edit.displayName || ""}
                          onChange={(e) => setEdit({ ...edit, displayName: e.target.value || null })}
                          placeholder="Nome mittente"
                        />
                      </label>
                      <label>
                        Colore
                        <input
                          className="input"
                          type="color"
                          value={edit.color}
                          onChange={(e) => setEdit({ ...edit, color: e.target.value })}
                          style={{ height: 42, padding: "0.2rem" }}
                        />
                      </label>
                      {!edit.hasOAuth && (
                        <>
                          <div className="form-grid-2">
                            <label>
                              IMAP host
                              <input
                                className="input"
                                value={edit.imapHost}
                                onChange={(e) => setEdit({ ...edit, imapHost: e.target.value })}
                                required
                              />
                            </label>
                            <label>
                              IMAP porta
                              <input
                                className="input"
                                type="number"
                                value={edit.imapPort}
                                onChange={(e) =>
                                  setEdit({ ...edit, imapPort: Number(e.target.value) || 993 })
                                }
                                required
                              />
                            </label>
                          </div>
                          <label className="check-row">
                            <input
                              type="checkbox"
                              checked={edit.imapSecure}
                              onChange={(e) => setEdit({ ...edit, imapSecure: e.target.checked })}
                            />
                            IMAP TLS/SSL
                          </label>
                          <div className="form-grid-2">
                            <label>
                              SMTP host
                              <input
                                className="input"
                                value={edit.smtpHost}
                                onChange={(e) => setEdit({ ...edit, smtpHost: e.target.value })}
                                required
                              />
                            </label>
                            <label>
                              SMTP porta
                              <input
                                className="input"
                                type="number"
                                value={edit.smtpPort}
                                onChange={(e) =>
                                  setEdit({ ...edit, smtpPort: Number(e.target.value) || 465 })
                                }
                                required
                              />
                            </label>
                          </div>
                          <label className="check-row">
                            <input
                              type="checkbox"
                              checked={edit.smtpSecure}
                              onChange={(e) => setEdit({ ...edit, smtpSecure: e.target.checked })}
                            />
                            SMTP TLS/SSL
                          </label>
                          <label>
                            Nuova password
                            <input
                              className="input"
                              type="password"
                              value={editPassword}
                              onChange={(e) => setEditPassword(e.target.value)}
                              placeholder="Lascia vuoto per non cambiare"
                              autoComplete="new-password"
                            />
                          </label>
                        </>
                      )}
                      {edit.hasOAuth && (
                        <p className="help">Account OAuth: host gestiti dal provider. Puoi aggiornare nome e colore.</p>
                      )}
                      <div className="actions">
                        <button className="btn btn-primary" type="submit" disabled={editBusy}>
                          {editBusy ? "Salvataggio…" : "Salva modifiche"}
                        </button>
                        <button
                          className="btn"
                          type="button"
                          onClick={() => {
                            setEditingId(null);
                            setEdit(null);
                            setEditError("");
                          }}
                        >
                          Annulla
                        </button>
                      </div>
                    </>
                  )}
                </form>
              )}
            </section>

            <section className="settings-block">
              <h3>OAuth provider</h3>
              <div className="actions">
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={async () => {
                    const { url } = await api.oauthStart("gmail");
                    window.location.href = url;
                  }}
                >
                  Collega Gmail
                </button>
                <button
                  className="btn"
                  type="button"
                  onClick={async () => {
                    const { url } = await api.oauthStart("microsoft");
                    window.location.href = url;
                  }}
                >
                  Collega Microsoft
                </button>
                <button
                  className="btn"
                  type="button"
                  onClick={async () => {
                    const h = await api.health();
                    setHealth(h.items);
                    toast.push({
                      message: `Health: ${h.items.filter((i) => i.status === "ok").length}/${h.items.length} ok`,
                    });
                  }}
                >
                  Riesegui health
                </button>
              </div>
              {msg && (
                <p
                  className={
                    msg.toLowerCase().includes("fail") || msg.toLowerCase().includes("error")
                      ? "error"
                      : "ok"
                  }
                >
                  {msg}
                </p>
              )}
            </section>
          </div>
        )}

        {wizardOpen && (
          <AccountWizard
            onClose={() => setWizardOpen(false)}
            onAdded={() => {
              setWizardOpen(false);
              void refreshAccounts();
              toast.push({ message: "Account aggiunto" });
            }}
          />
        )}

        {tab === "compose" && (
          <div className="settings-blocks">
            <section className="settings-block">
              <h3>Firme HTML</h3>
              <p className="help">La firma default viene aggiunta in coda all’invio.</p>
              <form
                className="form"
                onSubmit={async (e) => {
                  e.preventDefault();
                  const fd = new FormData(e.currentTarget);
                  await api.saveSignature({
                    accountId: String(fd.get("accountId") || "") || null,
                    name: String(fd.get("name") || "Default"),
                    html: String(fd.get("html") || ""),
                    isDefault: true,
                  });
                  const s = await api.signatures();
                  setSignatures(s.signatures);
                  toast.push({ message: "Firma salvata" });
                  e.currentTarget.reset();
                }}
              >
                <select className="select" name="accountId" defaultValue="">
                  <option value="">Tutti gli account</option>
                  {accounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.email}
                    </option>
                  ))}
                </select>
                <input className="input" name="name" placeholder="Nome firma" defaultValue="Default" />
                <textarea
                  className="input"
                  name="html"
                  rows={5}
                  placeholder="<p>Cordiali saluti,<br/>Nome</p>"
                  required
                />
                <button className="btn btn-primary" type="submit">
                  Salva firma default
                </button>
              </form>
              {signatures.length > 0 && (
                <div style={{ display: "grid", gap: "0.4rem" }}>
                  {signatures.map((s) => (
                    <div key={s.id} className="account-row">
                      <div>
                        <strong>{s.name}</strong>
                        <div style={{ color: "var(--muted)", fontSize: "0.8rem" }}>
                          {s.isDefault ? "default · " : ""}
                          {s.accountId || "globale"}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
            <section className="settings-block">
              <h3>Account predefinito invio</h3>
              <select
                className="select"
                value={prefs?.defaultAccountId || ""}
                onChange={(e) => void savePrefs({ defaultAccountId: e.target.value || null })}
              >
                <option value="">Nessuno</option>
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.email}
                  </option>
                ))}
              </select>
            </section>
          </div>
        )}

        {tab === "sync" && (
          <div className="settings-blocks">
            <section className="settings-block">
              <h3>IDLE worker</h3>
              <p className="help">Polling unseen in background per gli account aperti/preferiti.</p>
              <div className="actions">
                <button
                  className="btn btn-primary"
                  onClick={async () => {
                    await api.idleWatch(
                      accounts.map((a) => a.id),
                      true,
                    );
                    const st = await api.idleStatus();
                    toast.push({
                      message: `Watch ${st.watching.length} · unseen ${st.snapshots.map((s) => s.unseen).join(",")}`,
                    });
                  }}
                  disabled={!accounts.length}
                >
                  Abilita watch
                </button>
                <button
                  className="btn"
                  onClick={async () => {
                    await api.idleWatch(
                      accounts.map((a) => a.id),
                      false,
                    );
                    toast.push({ message: "IDLE disattivato" });
                  }}
                >
                  Disabilita
                </button>
              </div>
            </section>
            <section className="settings-block">
              <h3>JMAP</h3>
              <p className="help">Probe capacità JMAP dove il provider la espone (es. Fastmail).</p>
              <button
                className="btn"
                onClick={async () => {
                  if (!accounts[0]) return;
                  const j = await api.jmapSession(accounts[0].id);
                  toast.push({ message: j.available ? `JMAP: ${j.note}` : j.note });
                }}
                disabled={!accounts.length}
              >
                Probe sul primo account
              </button>
            </section>
            <section className="settings-block">
              <h3>Cache metadati</h3>
              <p className="help">La lista aggiorna una cache Postgres usata per ricerca locale rapida.</p>
              <button
                className="btn"
                onClick={async () => {
                  const q = window.prompt("Cerca in cache locale", "invoice");
                  if (!q) return;
                  const r = await api.cacheSearch(q);
                  toast.push({ message: `${r.messages.length} risultati in cache` });
                }}
              >
                Test ricerca cache
              </button>
            </section>
          </div>
        )}

        {tab === "privacy" && (
          <div className="settings-blocks">
            <section className="settings-block">
              <h3>Notifiche nuove email</h3>
              <p className="help">Suono “clack” e notifiche desktop quando arriva posta nuova.</p>
              <div className="pref-row">
                <div>
                  <strong>Notifiche PC</strong>
                  <span>
                    Permesso browser:{" "}
                    {typeof Notification !== "undefined" ? Notification.permission : "non supportato"}
                  </span>
                </div>
                <button
                  className={`btn ${notifyDesktop ? "btn-primary" : ""}`}
                  type="button"
                  onClick={() => {
                    void (async () => {
                      if (!notifyDesktop) {
                        const p = await requestNotifyPermission();
                        if (p !== "granted" && p !== "unsupported") {
                          toast.push({ message: "Permesso notifiche non concesso" });
                          return;
                        }
                      }
                      const next = !notifyDesktop;
                      setNotifyDesktop(next);
                      setNotifyDesktopEnabled(next);
                      toast.push({ message: next ? "Notifiche desktop on" : "Notifiche desktop off" });
                    })();
                  }}
                >
                  {notifyDesktop ? "On" : "Off"}
                </button>
              </div>
              <div className="pref-row">
                <div>
                  <strong>Suono arrivo</strong>
                  <span>Click breve quando crescono i non letti</span>
                </div>
                <div className="actions">
                  <button
                    className={`btn ${notifySound ? "btn-primary" : ""}`}
                    type="button"
                    onClick={() => {
                      const next = !notifySound;
                      setNotifySound(next);
                      setNotifySoundEnabled(next);
                      if (next) playMailClack();
                      toast.push({ message: next ? "Suono on" : "Suono off" });
                    }}
                  >
                    {notifySound ? "On" : "Off"}
                  </button>
                  <button className="btn" type="button" onClick={() => playMailClack()}>
                    Prova
                  </button>
                </div>
              </div>
            </section>
            <section className="settings-block">
              <h3>Contenuto remoto</h3>
              <div className="pref-row">
                <div>
                  <strong>Blocca immagini remote</strong>
                  <span>Preferenza browser (sandbox iframe già attiva)</span>
                </div>
                <button
                  className={`btn ${blockRemote ? "btn-primary" : ""}`}
                  onClick={() => {
                    const next = !blockRemote;
                    setBlockRemote(next);
                    localStorage.setItem("mailstorm.blockRemote", next ? "1" : "0");
                    toast.push({ message: next ? "Immagini remote bloccate" : "Immagini remote consentite" });
                  }}
                >
                  {blockRemote ? "Bloccate" : "Consentite"}
                </button>
              </div>
            </section>
            <section className="settings-block">
              <h3>Sessione</h3>
              <p className="help">Cookie HttpOnly + CSRF. Logout invalida la sessione server.</p>
              <button
                className="btn btn-danger"
                onClick={async () => {
                  await api.logout();
                  window.location.href = "/login";
                }}
              >
                Esci da tutte le sessioni locali
              </button>
            </section>
          </div>
        )}

        {tab === "shortcuts" && (
          <div className="settings-blocks">
            <section className="settings-block">
              <h3>Tastiera mail</h3>
              <div className="pref-row">
                <div>
                  <strong>j / k</strong>
                  <span>Messaggio successivo / precedente</span>
                </div>
              </div>
              <div className="pref-row">
                <div>
                  <strong>c / r</strong>
                  <span>Compone / Rispondi</span>
                </div>
              </div>
              <div className="pref-row">
                <div>
                  <strong>e / # / !</strong>
                  <span>Archivia / Elimina / Spam</span>
                </div>
              </div>
              <div className="pref-row">
                <div>
                  <strong>s / u</strong>
                  <span>Stella / Letto-non letto</span>
                </div>
              </div>
              <div className="pref-row">
                <div>
                  <strong>Ctrl+K oppure /</strong>
                  <span>Command palette globale</span>
                </div>
              </div>
            </section>
          </div>
        )}

        {tab === "admin" && user.role === "admin" && (
          <div className="settings-blocks">
            <section className="settings-block">
              <h3>Utenti registrati</h3>
              <p className="help">
                Clicca un utente per modificarlo (email, password, ruolo). Puoi aggiornare anche il tuo account
                amministratore.
              </p>
              <div style={{ display: "grid", gap: "0.45rem" }}>
                {adminUsers.map((u) => {
                  const isSelf = u.id === user.id;
                  const isOpen = editingUserId === u.id;
                  return (
                    <div key={u.id} className={`account-row ${isOpen ? "active" : ""}`}>
                      <button type="button" className="account-row-main" onClick={() => openUserEdit(u)}>
                        <span
                          className="dot"
                          style={{ background: u.role === "admin" ? "var(--accent)" : "var(--muted)" }}
                        />
                        <div>
                          <strong>
                            {u.displayName || u.email}
                            {isSelf ? " (tu)" : ""}
                          </strong>
                          <div style={{ color: "var(--muted)", fontSize: "0.85rem" }}>
                            {u.email} ·{" "}
                            <span className={u.role === "admin" ? "health-ok" : ""}>
                              {u.role === "admin" ? "Amministratore" : "Utente"}
                            </span>
                          </div>
                        </div>
                      </button>
                      <div className="actions">
                        <button className="btn" type="button" onClick={() => openUserEdit(u)}>
                          Modifica
                        </button>
                        {!isSelf && (
                          <button
                            className="btn btn-danger"
                            type="button"
                            onClick={() => void deleteUser(u.id, u.email)}
                          >
                            Elimina
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {editingUserId && (
                <form className="account-edit form" onSubmit={(e) => void saveUserEdit(e)}>
                  <h3>
                    Modifica utente
                    {editingUserId === user.id ? " (il tuo account)" : ""}
                  </h3>
                  {userError && <p className="error">{userError}</p>}
                  <label>
                    Email di accesso
                    <input
                      className="input"
                      type="email"
                      value={userForm.email}
                      onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                      required
                    />
                  </label>
                  <label>
                    Nome visualizzato
                    <input
                      className="input"
                      value={userForm.displayName}
                      onChange={(e) => setUserForm({ ...userForm, displayName: e.target.value })}
                      placeholder="Opzionale"
                    />
                  </label>
                  <label>
                    Ruolo
                    <select
                      className="select"
                      value={userForm.role}
                      onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                    >
                      <option value="user">Utente normale</option>
                      <option value="admin">Amministratore</option>
                    </select>
                  </label>
                  <label>
                    Nuova password
                    <input
                      className="input"
                      type="password"
                      value={userForm.password}
                      onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                      placeholder="Lascia vuoto per non cambiare"
                      minLength={8}
                      autoComplete="new-password"
                    />
                  </label>
                  <p className="help">Minimo 8 caratteri se imposti una nuova password.</p>
                  <div className="actions">
                    <button className="btn btn-primary" type="submit" disabled={userBusy}>
                      {userBusy ? "Salvataggio…" : "Salva modifiche"}
                    </button>
                    <button
                      className="btn"
                      type="button"
                      onClick={() => {
                        setEditingUserId(null);
                        setUserError("");
                      }}
                    >
                      Annulla
                    </button>
                  </div>
                </form>
              )}
            </section>

            <section className="settings-block">
              <h3>Crea nuovo utente</h3>
              <form className="form" onSubmit={createUser}>
                <input className="input" name="email" placeholder="email" required />
                <input
                  className="input"
                  name="password"
                  type="password"
                  placeholder="password (min. 8)"
                  minLength={8}
                  required
                />
                <input className="input" name="displayName" placeholder="nome visualizzato" />
                <select className="select" name="role" defaultValue="user">
                  <option value="user">Utente normale</option>
                  <option value="admin">Amministratore</option>
                </select>
                <button className="btn btn-primary" type="submit">
                  Crea utente
                </button>
              </form>
            </section>

            <section className="settings-block">
              <h3>Audit log</h3>
              <pre style={{ whiteSpace: "pre-wrap", color: "var(--muted)", fontSize: "0.8rem", margin: 0 }}>
                {JSON.stringify(audit.slice(0, 40), null, 2)}
              </pre>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
